import { memo, useCallback, useMemo, useState } from 'react';
import { CheckboxWithLabel, Processing, StickyContainer, Toggle } from '@wg/wows-react-uikit';

import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { scrollToY } from '~/helpers/animate';
import {
  showMembersOperationChangeCommanderDialog,
  showMembersOperationChangeRoleDialog,
  showMembersOperationRemoveDialog,
} from '~/helpers/dialogs';
import { t } from '~/helpers/localization';
import { getElementOffsetTop } from '~/helpers/position';
import { hasAnyPermission, PERMISSIONS } from '~/roles';
import { useAppDispatch } from '~/store';
import { openAccountProfile, openChatWindow } from '~/web2ClientAPI/base';
import { actionsClanProfile, fetchMembers } from '~/Actions/ActionClanProfile';

import { MembersControlBlock, MembersTable, StepCSSTransitionGroup } from '~/UIKit';
import Sticky from '~/UIKit/Sticky/Sticky';

import styles from './ClanMembers.module.scss';

import type { ICurrentAccountState } from '~/store/slices/currentAccountSlice';
import type { IClanMember, ITeamStatistics } from '~/types/declaration';

interface ClanMembersTableProps {
  clanId: number;
  currentAccount: ICurrentAccountState;
  disabledIds: number[];
  isFetchingMembers: boolean;
  members: IClanMember[];
  membersError?: string;
  selectedMemberIds?: number[];
  sort: {
    field: string;
    isAsc: boolean;
  };
  statistics?: ITeamStatistics;
}

const ClanMembersTable = ({
  clanId,
  currentAccount,
  disabledIds,
  isFetchingMembers,
  members: membersAll,
  membersError,
  selectedMemberIds = [],
  sort,
  statistics,
}: ClanMembersTableProps) => {
  const dispatch = useAppDispatch();

  const [isFilterOnlineOnly, setIsFilterOnlineOnly] = useState(false);

  const members = useMemo(() => {
    if (!isFilterOnlineOnly) {
      return membersAll;
    }

    return membersAll.filter((member) => {
      if (member.id === currentAccount.id) {
        return true;
      }

      return member.onlineStatus === true;
    });
  }, [currentAccount.id, isFilterOnlineOnly, membersAll]);

  const handleOnlineOnlyChange = useCallback((isChecked: boolean) => {
    console.log('handleOnlineOnlyChange', isChecked);
    setIsFilterOnlineOnly(isChecked);

    dwhExport.push(DWH_EVENTS.MEMBERS.TOGGLE_ONLINE_FILTER, {
      from: isChecked ? 'all' : 'online',
      to: isChecked ? 'online' : 'all',
    });
  }, []);

  const onChangeCommanderClick = useCallback(
    (newCommander: IClanMember) => {
      dispatch(showMembersOperationChangeCommanderDialog(newCommander));
    },
    [dispatch],
  );

  const onChangeRoleClick = useCallback(
    (memberToChangeId?: number) => {
      dispatch(showMembersOperationChangeRoleDialog(memberToChangeId));
    },
    [dispatch],
  );

  const onMembersReloadClick = useCallback(
    (clanId: number) => {
      void dispatch(fetchMembers(clanId));
    },
    [dispatch],
  );
  const onRemoveMembersClick = useCallback(
    (membersToRemove: IClanMember[]) => {
      dispatch(showMembersOperationRemoveDialog(membersToRemove));
    },
    [dispatch],
  );
  const onThSortClick = useCallback(
    (field: string, isAsc: boolean, hiddenSortingNames: Array<string>, clanId: number) => {
      dispatch(actionsClanProfile.toggleMembersSorting(clanId, field, isAsc, hiddenSortingNames));
    },
    [dispatch],
  );
  const onTickAllMembers = (members: Array<number>) => {
    dispatch(actionsClanProfile.toggleAllMembersTick(members));
  };
  const onTickMember = (member: number) => {
    dispatch(actionsClanProfile.toggleMemberTick(member));
  };

  const hasAnyClanManagementPermissions = useCallback(() => {
    if (!currentAccount.roleName) {
      return false;
    }

    return hasAnyPermission(currentAccount.roleName, [
      PERMISSIONS.CHANGE_ROLE,
      PERMISSIONS.CHANGE_COMMANDER,
      PERMISSIONS.REMOVE_MEMBER,
    ]);
  }, [currentAccount.roleName]);

  const handleThSortClick = useCallback(
    (field: string, isAsc: boolean, hiddenSortingNames: Array<string>) => {
      onThSortClick(field, isAsc, hiddenSortingNames, clanId);
    },
    [clanId, onThSortClick],
  );

  const handleMembersReloadClick = useCallback(() => {
    onMembersReloadClick(clanId);
  }, [clanId, onMembersReloadClick]);

  const scrollToOwn = useCallback((event: React.MouseEvent) => {
    event.preventDefault();

    const ownScrollEl = document.getElementById('ownScrollId');
    const appContainerEl = document.getElementById('app-container');
    const appEl = document.getElementById('app');

    if (!ownScrollEl || !appContainerEl || !appEl) {
      return;
    }

    const scrollTo =
      getElementOffsetTop(ownScrollEl) -
      getElementOffsetTop(appEl) -
      appContainerEl.offsetHeight / 2 -
      ownScrollEl.offsetHeight / 2;

    scrollToY(appEl, scrollTo);
  }, []);

  const handleRemoveMembersClick = useCallback(
    (membersToRemove?: IClanMember[]) => {
      const selected = membersToRemove ?? members.filter((member) => selectedMemberIds.includes(member.id));
      onRemoveMembersClick(selected);
    },
    [members, onRemoveMembersClick, selectedMemberIds],
  );

  const handleChangeCommanderClick = useCallback(
    (memberToDelegate?: IClanMember) => {
      if (memberToDelegate) {
        onChangeCommanderClick(memberToDelegate);
      } else if (selectedMemberIds.length === 1) {
        const member = members.find((member) => member.id === selectedMemberIds[0]);
        if (member) {
          onChangeCommanderClick(member);
        }
      }
    },
    [members, onChangeCommanderClick, selectedMemberIds],
  );

  const renderMembersControlBlock = () => {
    if (!hasAnyClanManagementPermissions() || membersError) {
      return null;
    }

    return (
      <Sticky
        usedStickyContainerId="members-table-control-block-sticky-container"
        isActive={selectedMemberIds.length > 0}
        forBottom
      >
        <div className={styles.membersControlBlock}>
          <MembersControlBlock
            clanId={clanId}
            isDisabled={isFetchingMembers}
            roleName={currentAccount.roleName}
            selectedMembersLength={selectedMemberIds.length}
            onRemoveMembersClick={handleRemoveMembersClick}
            onChangeCommanderClick={handleChangeCommanderClick}
            onChangeRoleClick={onChangeRoleClick}
          />
        </div>
      </Sticky>
    );
  };

  if (isFetchingMembers) {
    return <Processing isInside isFetching className={styles.processing} />;
  }

  if (currentAccount.clanId === clanId) {
    return (
      <StepCSSTransitionGroup level={3}>
        <StickyContainer key="members-table" id="members-table-control-block-sticky-container">
          {!membersError && (
            <div className={styles.filters}>
              <Toggle caption={t('Перейти к моей позиции')} onClick={scrollToOwn} />
              <CheckboxWithLabel
                labelText={t('Show online players only')}
                isChecked={isFilterOnlineOnly}
                onChange={handleOnlineOnlyChange}
              />
            </div>
          )}
          <MembersTable
            clanId={clanId}
            currentAccount={currentAccount}
            currentBattleType={statistics?.currentBattleType}
            disabledIds={disabledIds}
            displayCheckboxes={hasAnyClanManagementPermissions()}
            error={membersError}
            isContextMenuEnabled
            isFetching={isFetchingMembers}
            members={members}
            selectedMemberIds={selectedMemberIds}
            sort={sort}
            stickyContainerId="members-table-sticky-container"
            onChangeCommanderClick={handleChangeCommanderClick}
            onChangeRoleClick={onChangeRoleClick}
            onReloadClick={handleMembersReloadClick}
            onRemoveMembersClick={handleRemoveMembersClick}
            onThSortClick={handleThSortClick}
            onTickAllMembers={onTickAllMembers}
            onTickMember={onTickMember}
            openAccountProfile={openAccountProfile}
            openChatWindow={openChatWindow}
          />
          {renderMembersControlBlock()}
        </StickyContainer>
      </StepCSSTransitionGroup>
    );
  }

  return (
    <StepCSSTransitionGroup className={styles.tableWrapper} level={3}>
      <StickyContainer key="members-table" id="members-table-control-block-sticky-container">
        <MembersTable
          key="members-table"
          clanId={clanId}
          currentAccount={currentAccount}
          currentBattleType={statistics?.currentBattleType}
          error={membersError}
          isFetching={isFetchingMembers}
          members={members}
          sort={sort}
          stickyContainerId="members-table-sticky-container"
          onReloadClick={handleMembersReloadClick}
          onThSortClick={handleThSortClick}
          openAccountProfile={openAccountProfile}
          openChatWindow={openChatWindow}
        />
      </StickyContainer>
    </StepCSSTransitionGroup>
  );
};

export default memo(ClanMembersTable);
